import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled from 'styled-components'

import { Span } from '@/@bothrs-ui/web/components/typography/Body'

import type { ComponentPropsWithoutRef } from 'react'

/**
 * One row of a list: a round badge with an icon, a title, a line of description and a short piece
 * of meta text (a date, a status) at the end. The root is a grid, so every part is atomic and sits
 * in its own area; a call site composes the parts it has:
 *
 *   badge | title       | aside
 *   badge | description | meta
 *
 * The task, message and document rows compose it; nothing here knows what the row is about.
 */

/* ------------------------------------------------------------------------------------------------- *
 * ListRow
 * ------------------------------------------------------------------------------------------------- */

type ListRowRootProps = ComponentPropsWithoutRef<'span'>

const ListRowRoot = ({ children, ...props }: ListRowRootProps) => <StRow {...props}>{children}</StRow>

/* ------------------------------------------------------------------------------------------------- *
 * ListRow.Badge
 * ------------------------------------------------------------------------------------------------- */

// The round frame in front of the row. Takes an Icon, or an image for a partner's own mark.
type ListRowBadgeProps = ComponentPropsWithoutRef<'span'>

const ListRowBadge = ({ children, ...props }: ListRowBadgeProps) => <StBadge {...props}>{children}</StBadge>

/* ------------------------------------------------------------------------------------------------- *
 * ListRow.Icon
 * ------------------------------------------------------------------------------------------------- */

// The icon inside the badge, in the action colour. The icon comes from the call site.
type ListRowIconProps = ComponentPropsWithoutRef<typeof FontAwesomeIcon>

const ListRowIcon = (props: ListRowIconProps) => <StIcon {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * ListRow.Title
 * ------------------------------------------------------------------------------------------------- */

type ListRowTitleProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'>

const ListRowTitle = ({ children, ...props }: ListRowTitleProps) => (
  <StTitle type='BodySmallBold' {...props}>
    {children}
  </StTitle>
)

/* ------------------------------------------------------------------------------------------------- *
 * ListRow.Description
 * ------------------------------------------------------------------------------------------------- */

type ListRowDescriptionProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'>

const ListRowDescription = ({ children, ...props }: ListRowDescriptionProps) => (
  <StDescription type='BodySmallRegular' {...props}>
    {children}
  </StDescription>
)

/* ------------------------------------------------------------------------------------------------- *
 * ListRow.Meta
 * ------------------------------------------------------------------------------------------------- */

// The short text at the end of the description line: a date, a status. Keeps to one line.
type ListRowMetaProps = Omit<ComponentPropsWithoutRef<typeof Span>, 'type'>

const ListRowMeta = ({ children, ...props }: ListRowMetaProps) => (
  <StMeta type='BodySmallRegular' {...props}>
    {children}
  </StMeta>
)

/* ------------------------------------------------------------------------------------------------- *
 * ListRow.UnreadDot
 * ------------------------------------------------------------------------------------------------- */

// The dot beside the title that marks an unread item.
type ListRowUnreadDotProps = ComponentPropsWithoutRef<'span'>

const ListRowUnreadDot = (props: ListRowUnreadDotProps) => <StUnreadDot {...props} />

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const ListRow = Object.assign(ListRowRoot, {
  Badge: ListRowBadge,
  Icon: ListRowIcon,
  Title: ListRowTitle,
  Description: ListRowDescription,
  Meta: ListRowMeta,
  UnreadDot: ListRowUnreadDot,
})

export { ListRow }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

// Spans rather than divs, so a row can sit inside a button (a button only takes phrasing content).
const StRow = styled.span`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    'badge title aside'
    'badge description meta';
  align-items: center;
  column-gap: ${({ theme }) => theme.UI.SpacingPx.Space3};
  row-gap: ${({ theme }) => theme.UI.SpacingPx.Space1};
  width: 100%;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StBadge = styled.span`
  grid-area: badge;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.UI.SpacingPx.Space11};
  height: ${({ theme }) => theme.UI.SpacingPx.Space11};
  border: 1px solid ${({ theme }) => theme.Colors.Neutrals[50]};
  border-radius: 50%;
`

const StIcon = styled(FontAwesomeIcon)`
  font-size: ${({ theme }) => theme.UI.IconSize.Medium}px;
  color: ${({ theme }) => theme.Components.Button.Tertiary.Text};
`

// The text parts truncate rather than wrap: a row is one line of title and one of description.
const truncate = `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StTitle = styled(Span)`
  grid-area: title;
  ${truncate}
`

const StDescription = styled(Span)`
  grid-area: description;
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
  ${truncate}
`

const StMeta = styled(Span)`
  grid-area: meta;
  justify-self: end;
  color: ${({ theme }) => theme.Colors.Neutrals[500]};
  white-space: nowrap;
`

const StUnreadDot = styled.span`
  grid-area: aside;
  width: ${({ theme }) => theme.UI.SpacingPx.Space2};
  height: ${({ theme }) => theme.UI.SpacingPx.Space2};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.Colors.Status.Error};
`
